import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ADMIN_RUNTIME_COMMANDS } from '../../app/runtime/adminRuntime';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import {
  isIndexingActive,
  normalizeIndexingSnapshot,
  normalizeStartedJob,
} from './indexingRuntimeUtils';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';

const POLL_INTERVAL_MS = 5000;

export function useRuntimeIndexing({ historyLimit = 30, enabled = true } = {}) {
  const t = useLocalizedMessage();
  const { runtime } = useShellAppContext();
  const currentShop = useSelector(state => state.app?.currentShop);
  const [snapshot, setSnapshot] = useState({ current: null, history: [], lastSuccessAt: null });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!enabled) {
      setIsLoading(false);
      setIsRefreshing(false);
      setError(null);
      return null;
    }
    if (!runtime || !currentShop) {
      setIsLoading(false);
      setError(t('catalog.update.serviceUnavailable'));
      return null;
    }

    if (quiet) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      const [historyResponse, statusResponse] = await Promise.all([
        runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.INDEXING_HISTORY, {
          shopId: currentShop,
          limit: historyLimit,
        }),
        runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.INDEXING_STATUS, {
          shopId: currentShop,
        }),
      ]);
      if (historyResponse?.result === 'error') {
        throw new Error(historyResponse.error || t('catalog.update.historyLoadFailed'));
      }
      if (statusResponse?.result === 'error') {
        throw new Error(statusResponse.error || t('catalog.update.statusLoadFailed'));
      }
      const nextSnapshot = normalizeIndexingSnapshot(historyResponse, statusResponse);
      if (mountedRef.current) {
        setSnapshot(nextSnapshot);
        setError(null);
      }
      return nextSnapshot;
    } catch (requestError) {
      if (mountedRef.current) {
        setError(requestError?.message || requestError?.error || t('catalog.update.loadFailed'));
      }
      return null;
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [currentShop, enabled, historyLimit, runtime, t]);

  useEffect(() => {
    mountedRef.current = true;
    setSnapshot({ current: null, history: [], lastSuccessAt: null });
    setError(null);
    setIsLoading(enabled);
    if (!enabled) return undefined;
    refresh();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh({ quiet: true });
    }, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      window.clearInterval(intervalId);
    };
  }, [enabled, refresh]);

  const start = useCallback(async () => {
    if (!enabled || !runtime || !currentShop || isStarting || isIndexingActive(snapshot.current)) return null;
    setIsStarting(true);
    setError(null);
    try {
      const response = await runtime.sendMessageWithResponse(
        ADMIN_RUNTIME_COMMANDS.INDEXING_START,
        { shopId: currentShop },
      );
      if (response?.result === 'error') {
        throw new Error(response.error || t('catalog.update.startFailed'));
      }
      const startedJob = normalizeStartedJob(response);
      if (mountedRef.current) {
        setSnapshot(previous => ({
          ...previous,
          current: { ...startedJob, status: startedJob.status === 'idle' ? 'queued' : startedJob.status },
        }));
      }
      return startedJob;
    } catch (requestError) {
      if (mountedRef.current) {
        setError(requestError?.message || requestError?.error || t('catalog.update.startFailed'));
      }
      return null;
    } finally {
      if (mountedRef.current) setIsStarting(false);
    }
  }, [currentShop, enabled, isStarting, runtime, snapshot.current, t]);

  return {
    snapshot,
    isLoading,
    isRefreshing,
    isStarting,
    error,
    refresh,
    start,
  };
}
